/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Plus, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { tasksApi, PRIORITY_COLORS, PRIORITY_LABELS } from './tasksApi';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

/**
 * Mind-map style Epic view. Each epic = central node. Tasks linked via big_picture_id branch out radially.
 * SVG-based — no external graph library needed.
 */
export const EpicView = ({ onOpenTask }) => {
  const { workAreaNames } = useWorkAreas();
  const [epics, setEpics] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEpicId, setActiveEpicId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const [e, t] = await Promise.all([tasksApi.listEpics(), tasksApi.listTasks()]);
      setEpics(e || []);
      setTasks(t || []);
      if ((e || []).length > 0 && !activeEpicId) setActiveEpicId(e[0].item_id);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeEpic = epics.find((e) => e.item_id === activeEpicId);
  const epicTasks = useMemo(
    () => tasks.filter((t) => t.big_picture_id === activeEpicId),
    [tasks, activeEpicId]
  );

  const createEpic = async (body) => {
    setBusy(true);
    try {
      const created = await tasksApi.createBpItem({
        type: 'EPIC',
        title: body.title,
        description: body.description,
        status: 'planned',
      });
      setShowCreate(false);
      toast.success('Epic created');
      await load();
      setActiveEpicId(created.item_id);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // Compute radial layout
  const layout = useMemo(() => {
    if (!activeEpic) return null;
    const center = { x: 600, y: 300 };
    const N = epicTasks.length;
    const ringRadius = Math.max(180, 80 + N * 14);
    const nodes = epicTasks.map((task, i) => {
      const angle = (i / Math.max(N, 1)) * Math.PI * 2 - Math.PI / 2;
      return {
        task,
        x: center.x + Math.cos(angle) * ringRadius,
        y: center.y + Math.sin(angle) * ringRadius,
      };
    });
    return { center, nodes, ringRadius };
  }, [activeEpic, epicTasks]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.max(0.3, Math.min(3, z * delta)));
  };

  const onMouseDown = (e) => {
    dragRef.current = { dragging: true, startX: e.clientX - pan.x, startY: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  return (
    <div data-testid="epic-view" className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="epic-new"><Plus className="w-3 h-3 mr-1" /> New Epic</Button>
        <div className="flex gap-1 overflow-x-auto flex-1">
          {epics.map((e) => (
            <Button key={e.item_id} size="sm" variant={activeEpicId === e.item_id ? 'default' : 'outline'}
              className="h-7 text-xs whitespace-nowrap" onClick={() => setActiveEpicId(e.item_id)}
              data-testid={`epic-pill-${e.item_id}`}>
              {e.title}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setZoom((z) => Math.min(3, z * 1.2))} data-testid="epic-zoom-in"><ZoomIn className="w-3 h-3" /></Button>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setZoom((z) => Math.max(0.3, z / 1.2))} data-testid="epic-zoom-out"><ZoomOut className="w-3 h-3" /></Button>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} data-testid="epic-reset"><RotateCcw className="w-3 h-3" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading
        </div>
      ) : !activeEpic ? (
        <Card className="p-12 text-center text-sm text-muted-foreground">
          No epics yet. Create one — Big Picture EPIC items show up here as mind-maps with their linked tasks.
        </Card>
      ) : (
        <Card
          className="bg-gradient-to-br from-slate-50 to-violet-50 dark:from-slate-900 dark:to-violet-950 border-border overflow-hidden cursor-grab active:cursor-grabbing"
          style={{ height: 'calc(100vh - 290px)' }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          data-testid="epic-canvas"
        >
          <svg width="100%" height="100%" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid meet">
            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Edges from center to each task */}
              {layout && layout.nodes.map((n, i) => (
                <line
                  key={`edge-${i}`}
                  x1={layout.center.x} y1={layout.center.y}
                  x2={n.x} y2={n.y}
                  stroke="rgb(139, 92, 246)" strokeWidth="1.5" strokeOpacity="0.4"
                />
              ))}

              {/* Center epic node */}
              {layout && (
                <g transform={`translate(${layout.center.x}, ${layout.center.y})`}>
                  <circle r="60" fill="url(#epicGrad)" stroke="rgb(139, 92, 246)" strokeWidth="3" />
                  <foreignObject x="-58" y="-30" width="116" height="60">
                    <div className="flex items-center justify-center h-full text-center px-1">
                      <div>
                        <div className="text-[9px] font-semibold text-violet-600 dark:text-violet-300 uppercase">Epic</div>
                        <div className="text-[11px] font-bold text-foreground line-clamp-2">{activeEpic.title}</div>
                      </div>
                    </div>
                  </foreignObject>
                </g>
              )}

              {/* Task nodes */}
              {layout && layout.nodes.map(({ task, x, y }) => {
                const wa = workAreaNames[task.work_area_id];
                return (
                  <g key={task.task_id} transform={`translate(${x}, ${y})`} className="cursor-pointer"
                    onClick={() => onOpenTask && onOpenTask(task)} data-testid={`epic-node-${task.task_id}`}>
                    <rect x="-70" y="-26" width="140" height="52" rx="6"
                      fill="white" className="dark:fill-slate-800"
                      stroke={task.task_status === 'Done' ? 'rgb(34, 197, 94)' : 'rgb(148, 163, 184)'}
                      strokeWidth="1.5" />
                    <foreignObject x="-66" y="-22" width="132" height="44">
                      <div className="text-center">
                        <div className="flex justify-center gap-1 mb-0.5">
                          <span className={cn('text-[8px] font-bold px-1 rounded', PRIORITY_COLORS[task.task_priority ?? 3])}>
                            {PRIORITY_LABELS[task.task_priority ?? 3]}
                          </span>
                        </div>
                        <div className="text-[10px] font-medium text-foreground line-clamp-2 leading-tight">{task.task_title}</div>
                      </div>
                    </foreignObject>
                  </g>
                );
              })}

              <defs>
                <radialGradient id="epicGrad">
                  <stop offset="0%" stopColor="rgba(139, 92, 246, 0.3)" />
                  <stop offset="100%" stopColor="rgba(99, 102, 241, 0.1)" />
                </radialGradient>
              </defs>
            </g>
          </svg>

          {layout && epicTasks.length === 0 && (
            <div className="absolute inset-0 flex items-end justify-center pb-12 pointer-events-none">
              <div className="text-xs text-muted-foreground bg-card border border-border rounded-md px-3 py-2">
                No tasks linked to this epic yet. Set <code>big_picture_id = {activeEpic.item_id}</code> on tasks to attach them.
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="epic-create-dialog">
          <DialogHeader><DialogTitle>New Epic</DialogTitle></DialogHeader>
          <EpicForm onSubmit={createEpic} disabled={busy} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EpicForm = ({ onSubmit, disabled }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Epic title" data-testid="epic-title-input" />
      <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" data-testid="epic-description-input" />
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, description })} disabled={!title || disabled} data-testid="epic-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default EpicView;
